import { twJoin } from 'tailwind-merge';

import RichText from 'molecules/RichText';
import { hasRichText } from 'molecules/RichText/utils';

import AccordionItem from 'components/Accordion/component/AccordionItem';
import { getFaqSchema, isFaqSection } from 'components/Accordion/utils';
import { ArticleAccordionConfigProps } from 'components/ArticleAccordion/config';

import { hasArrayValues } from 'utils/array/arrays';

import type { FC } from 'react';

// Measured against a live blog post: article headings render at 33px / weight
// 700 / inherited black in Space Grotesk, from RichText's headingStyles
// (`pt-4 font-bold` + the text-rich-* step). The page-section Accordion heads
// its block with the Heading molecule instead, whose smallest step is
// font-semibold at text-display-lg (48px), which reads as a new page rather
// than a section of this article. Match the article by default, per heading
// level, and keep the section treatment available.
const articleHeadingSizes = {
  h2: 'text-rich-h2',
  h3: 'text-rich-h3',
  h4: 'text-rich-h4',
} as const;

const LARGE_HEADING = 'font-semibold text-display-sm sm:text-display-lg';

// Same names and ordering as the page Section's paddingTop/paddingBottom, at
// the scale an in-article block needs rather than a full-bleed page section.
// The <article> is a flex column with gap-6, so 24px is always present on top
// of whatever is picked here.
const spaceBeforeClasses = {
  none: 'pt-0',
  xs: 'pt-2',
  sm: 'pt-4',
  md: 'pt-6',
  lg: 'pt-10',
  xl: 'pt-16',
  '2xl': 'pt-24',
} as const;

const spaceAfterClasses = {
  none: 'pb-0',
  xs: 'pb-2',
  sm: 'pb-4',
  md: 'pb-6',
  lg: 'pb-10',
  xl: 'pb-16',
  '2xl': 'pb-24',
} as const;

const ArticleAccordion: FC<ArticleAccordionConfigProps> = ({
  accordionItems,
  hasFaqSchema,
  heading,
  headingTag = 'h2',
  headingSize = 'article',
  subheading,
  spaceBefore = 'sm',
  spaceAfter = 'lg',
  htmlId = 'article-accordion',
  className,
  isBulletStandard = true,
}) => {
  const HeadingTag = (headingTag || 'h2') as 'h2' | 'h3' | 'h4';

  const hasHeading = Boolean(heading);
  const hasSubheading = hasRichText(subheading);

  // Article headings carry no colour of their own, they inherit it. Matching
  // that (rather than setting text-heading) is what keeps this heading the same
  // black as the ones above and below it.
  const headingClass = headingSize === 'large' ? LARGE_HEADING : twJoin('font-bold', articleHeadingSizes[HeadingTag]);

  // Same behaviour as the page-section Accordion: emit FAQ schema when the
  // editor asked for it, or when the heading reads as an FAQ. Blog posts and
  // guides are where FAQ blocks earn the most, so this carries over as-is.
  const shouldEmitFaqSchema = hasFaqSchema || isFaqSection(heading);
  const faqSchema = shouldEmitFaqSchema && hasArrayValues(accordionItems) && getFaqSchema(accordionItems, heading);

  // The page-section Accordion puts 40-64px between its heading block and the
  // first item. Per the ticket that space goes away when there is no subheading,
  // so the heading sits on the first item. It is mt-2 rather than a literal 0
  // because the first item renders open, and its white card edge lands on the
  // heading's descenders at 0 (the "g" in "integrations" gets clipped).
  const accordionSpacing = hasHeading && hasSubheading ? 'mt-6' : 'mt-2';

  return (
    <>
      {faqSchema && Object.keys(faqSchema).length && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      )}
      <div
        id={htmlId}
        className={twJoin(
          'flex w-full flex-col text-left',
          spaceBeforeClasses[spaceBefore],
          spaceAfterClasses[spaceAfter],
          className,
        )}
      >
        {hasHeading && <HeadingTag className={headingClass}>{heading}</HeadingTag>}

        {hasSubheading && (
          <RichText
            text={subheading}
            className="mt-3 text-lg font-medium text-gray-700 dark:text-gray-300"
            isBulletStandard={isBulletStandard}
          />
        )}

        {hasArrayValues(accordionItems) && (
          <AccordionItem
            accordionItems={accordionItems}
            className={accordionSpacing}
            isBulletStandard={isBulletStandard}
          />
        )}
      </div>
    </>
  );
};

export default ArticleAccordion;
